import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { ChopAlterskategorie } from '@entity/chop';
import { ChopService } from '@service/chop.service';
import { FormButtonBarComponent } from '@shared/form-button-bar/form-button-bar.component';
import { FormNumberInputComponent } from '@shared/form-number-input/form-number-input.component';
import { FormTextInputComponent } from '@shared/form-text-input/form-text-input.component';
import { applyChopServerValidationError } from '../../chop-server-errors';

const FIELD_MAP = { Code: 'code', 'Bezeichnung.De': 'bezeichnung.De' } as const;

@Component({
  selector: 'app-chop-alterskategorie-dialog',
  templateUrl: './chop-alterskategorie-dialog.component.html',
  styleUrls: ['./chop-alterskategorie-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ReactiveFormsModule, FormTextInputComponent, FormNumberInputComponent, FormButtonBarComponent],
})
export class ChopAlterskategorieDialogComponent {
  private formBuilder = inject(FormBuilder);
  private dialogConfig = inject(DynamicDialogConfig);
  private dialogRef = inject(DynamicDialogRef);
  private chopService = inject(ChopService);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);

  private katalogId: number = this.dialogConfig.data.katalogId;
  private alterskategorie: ChopAlterskategorie | undefined = this.dialogConfig.data.alterskategorie;
  form = this.formBuilder.nonNullable.group({
    code: [this.alterskategorie?.code ?? '', Validators.required],
    bezeichnung: this.formBuilder.nonNullable.group({
      De: [this.alterskategorie?.bezeichnung?.De ?? '', Validators.required],
      Fr: [this.alterskategorie?.bezeichnung?.Fr ?? ''],
      It: [this.alterskategorie?.bezeichnung?.It ?? ''],
    }),
    altersVon: [this.alterskategorie?.altersVon ?? 0, Validators.required],
    altersBis: [this.alterskategorie?.altersBis as number | null],
  });
  submitting = false;

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitting = true;
    const value = this.form.getRawValue() as ChopAlterskategorie;
    const action = this.alterskategorie
      ? this.chopService.updateAlterskategorie(this.alterskategorie.id, { ...this.alterskategorie, ...value })
      : this.chopService.createAlterskategorie(this.katalogId, value);
    action.pipe(finalize(() => (this.submitting = false))).subscribe({
      next: (result) => this.dialogRef.close(result),
      error: (err) => {
        if (!applyChopServerValidationError(this.form, err?.error, FIELD_MAP)) {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('GLOBAL.NOT_SUCCESSFULLY_SAVED'),
            detail: err?.error?.message,
          });
        }
      },
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
